import { useState } from 'react'
import { Copy, MoreHorizontal, Pencil, Shield, Trash2 } from 'lucide-react'
import { toast } from 'sonner'
import type { SavedFilterDocument } from '@/lib/db/rxdb/hooks'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { RenameFilterDialog } from '@/components/features/saved-filters/RenameFilterDialog'
import { ChangePermissionDialog } from '@/components/features/saved-filters/ChangePermissionDialog'
import { useSavedFilterMutations } from '@/lib/db/rxdb/hooks'
import useLoginStore from '@/store/LoginStore'

interface ViewRowActionsProps {
  view: SavedFilterDocument
  /** Optional workspace role — controls whether non-owners can edit shared views. */
  memberRole?: string | null
}

export function ViewRowActions({ view, memberRole }: ViewRowActionsProps) {
  const [showRename, setShowRename] = useState(false)
  const [showPermission, setShowPermission] = useState(false)
  const [showDelete, setShowDelete] = useState(false)

  const { deleteSavedFilter, createSavedFilter } = useSavedFilterMutations()
  const userId = useLoginStore((s) => s.userId)

  const isOwner = view.ownerId === userId
  const isAdmin = memberRole === 'admin'
  const canEdit = isOwner || isAdmin

  const handleDuplicate = async () => {
    if (!userId) return
    try {
      await createSavedFilter({
        id: crypto.randomUUID(),
        name: `${view.name} (copy)`,
        description: view.description,
        filterConfig: view.filterConfig,
        layoutConfig: view.layoutConfig ?? null,
        scope: view.scope,
        scopeEntityId: view.scopeEntityId,
        permission: 'private',
        ownerId: userId,
        icon: view.icon,
        sortOrder: null,
      })
      toast.success('View duplicated')
    } catch {
      toast.error('Failed to duplicate view')
    }
  }

  const handleDelete = async () => {
    try {
      await deleteSavedFilter(view.id)
      toast.success('View deleted')
    } catch {
      toast.error('Failed to delete view')
    }
  }

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            aria-label="View actions"
            className="flex h-7 w-7 items-center justify-center rounded hover:bg-accent text-muted-foreground hover:text-foreground"
            onClick={(e) => e.stopPropagation()}
          >
            <MoreHorizontal className="size-4" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48">
          <DropdownMenuItem onSelect={() => { void handleDuplicate() }}>
            <Copy className="mr-2 size-4" />
            Duplicate
          </DropdownMenuItem>
          {canEdit && (
            <>
              <DropdownMenuSeparator />
              <DropdownMenuItem onSelect={() => setShowRename(true)}>
                <Pencil className="mr-2 size-4" />
                Rename
              </DropdownMenuItem>
              <DropdownMenuItem onSelect={() => setShowPermission(true)}>
                <Shield className="mr-2 size-4" />
                Change permission
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                className="text-destructive focus:text-destructive"
                onSelect={() => setShowDelete(true)}
              >
                <Trash2 className="mr-2 size-4" />
                Delete
              </DropdownMenuItem>
            </>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <RenameFilterDialog
        open={showRename}
        onOpenChange={setShowRename}
        filterId={view.id}
        currentName={view.name}
      />

      <ChangePermissionDialog
        open={showPermission}
        onOpenChange={setShowPermission}
        filterId={view.id}
        currentPermission={view.permission}
      />

      <AlertDialog open={showDelete} onOpenChange={setShowDelete}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete view</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &quot;{view.name}&quot;? This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => { void handleDelete() }}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
